import React, { useState, useRef } from 'react';
import { X, Menu, Star, Zap, Briefcase, Users, Sparkles, ChevronDown, Send, CheckCircle, CalendarIcon } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';

const PROMO_VIDEO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/promo.mp4`;

const EVENT_TYPES = ['Conference', 'Awards Banquet', 'Holiday Party', 'Client Appreciation Event', 'Annual Meeting', 'Leadership Retreat', 'Other'];
const AUDIENCE_SIZES = ['Under 50', '50–100', '100–250', '250–500', '500+'];
const HEARD_FROM = ['Referral', 'Google', 'LinkedIn', 'Social Media', 'Previous Performance', 'Other'];

const testimonials = [
  { text: "Ryan was an absolute joy to work with from start to finish. Ryan's ability to captivate an audience made the evening a memorable one!", author: 'Elise', event: 'Mimslyn Inn' },
  { text: "He's a great performer both in platform type shows and in closeup/strolling settings.", author: 'Jobeth Bowers', event: 'ElktonMagic Coordinator' },
  { text: 'Because of him I had the event of the year!', author: 'Bianca A.', event: 'Event Performance' },
  { text: 'The crowd loved him. People still ask me at parties how he did certain tricks.', author: 'Tammy Ruggiero', event: 'Crone and Crow Show' },
  { text: 'We had a blast with Ryan!!', author: 'Hallie R.', event: 'Event Performance' },
  { text: 'Ryan sets the bar high when it comes to comedic interactive performances.', author: 'Jordan B.', event: 'Event Performance' },
];

const faqs = [
  { q: 'How long is the show?', a: 'The show works in 15, 20, 30, or 45 minutes, based on the needs of your event!' },
  { q: 'What size audience can you perform for?', a: 'My show works for anywhere from 10 to 10,000 people!' },
  { q: 'Can the show be customized?', a: 'Absolutely! If you have a theme or company branding you want to be included, feel free to share, and I will do my best to work with it!' },
  { q: 'What are your technical requirements?', a: 'I only require a basic mic set up, basic lighting, and a basic stage. Anything else is just a bonus!' },
  { q: 'Is the show appropriate for all audiences?', a: 'Yes! This show is designed to play in any venue for any audience!' },
  { q: 'Do you travel?', a: 'Gladly! We can discuss those options, as well as any other needs or concerns you may have, when you reach out!' },
];

const whyItWorks = [
  { icon: Zap, title: 'Interactive', text: "Your guests aren't watching. They're participating" },
  { icon: Briefcase, title: 'Professional', text: 'Designed for conferences, banquets, and company events' },
  { icon: Users, title: 'Scalable', text: 'Works for intimate executive dinners and large conference ballrooms alike' },
  { icon: Sparkles, title: 'Memorable', text: 'The kind of shared experience people are still talking about the next morning' },
];

const perfectFor = ['Conferences', 'Awards Banquets', 'Holiday Parties', 'Client Appreciation Events', 'Leadership Retreats', 'Annual Meetings'];

const Corporate = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phone: '', eventType: '', eventDate: null, audienceSize: '', eventLocation: '', message: '', heardFrom: ''
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const [first, ...rest] = formData.name.trim().split(' ');
    const payload = {
      firstName: first || 'Corporate',
      lastName: rest.join(' ') || 'Inquiry',
      email: formData.email,
      subject: `Corporate Booking – ${formData.eventType || 'Event'} – ${formData.company || 'Company'}`,
      message: [
        `Name: ${formData.name}`,
        `Company: ${formData.company}`,
        `Phone: ${formData.phone}`,
        `Event Type: ${formData.eventType}`,
        `Preferred Date: ${formData.eventDate ? format(formData.eventDate, 'PPP') : 'Not specified'}`,
        `Estimated Audience Size: ${formData.audienceSize}`,
        `Event Location: ${formData.eventLocation}`,
        `How they heard about you: ${formData.heardFrom}`,
        '',
        'Message:',
        formData.message,
      ].join('\n'),
    };
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setQuoteOpen(false);
          setFormData({ name: '', company: '', email: '', phone: '', eventType: '', eventDate: null, audienceSize: '', eventLocation: '', message: '', heardFrom: '' });
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting form', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-white relative" style={{ background: 'linear-gradient(to bottom, #000000 0%, #0a0a0a 8%, #0f0f0f 15%, #0c0c0c 25%, #090909 40%, #060606 55%, #040404 70%, #020202 85%, #000000 100%)' }}>
      {/* Subtle pink glows */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[10%] -left-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[35%] -right-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[65%] -left-64 w-[700px] h-[1000px] bg-pink-500/[0.015] rounded-full blur-[200px]"></div>
      </div>

      {/* Navigation - matches Home */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b border-white/5 bg-black/80">
        <div className="max-w-7xl mx-auto px-6 py-0">
          <div className="flex items-center justify-between">
            <a href="/" aria-label="Home" className="cursor-pointer" data-testid="corp-logo-home">
              <img
                src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/mjuj40fa_Ryan%20daniels%20mentalist-4.png"
                alt="Ryan Daniels Mentalist"
                style={{ height: '140px', marginTop: '-8px', marginBottom: '-8px' }}
              />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="nav-link" data-testid="corp-nav-home">Home</a>
              <a href="/#testimonials" className="nav-link">Testimonials</a>
              <a href="/#about" className="nav-link">About</a>
              <a href="/corporate" className="nav-link">Corporate</a>
              <button onClick={() => setQuoteOpen(true)} className="cta-button" data-testid="corp-nav-book">Request Availability</button>
            </div>
            <button className="md:hidden text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu" data-testid="corp-mobile-menu-btn">
              <Menu size={28} />
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 flex flex-col gap-3">
              <a href="/" className="nav-link text-left py-2">Home</a>
              <a href="/#testimonials" className="nav-link text-left py-2">Testimonials</a>
              <a href="/#about" className="nav-link text-left py-2">About</a>
              <a href="/corporate" className="nav-link text-left py-2">Corporate</a>
              <button onClick={() => { setMobileMenuOpen(false); setQuoteOpen(true); }} className="cta-button w-full">Request Availability</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 overflow-hidden pt-32 md:pt-28 md:pb-16 md:min-h-[85vh] md:flex md:items-center md:justify-center" data-testid="corp-hero">
        {/* Background image — mobile: fixed shorter container height, image anchored to bottom so the TOP is cropped (still above the subject's head); desktop: image fills the min-h section */}
        <div className="relative w-full h-[200px] overflow-hidden md:h-auto md:absolute md:inset-0 md:z-0">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/kg0i9vza_Screenshot%202026-07-19%20at%202.56.01%E2%80%AFPM.webp"
            alt="Ryan Daniels corporate performance"
            className="absolute bottom-0 left-0 w-[112%] h-auto max-w-none -ml-[12%] md:relative md:bottom-auto md:left-auto md:ml-0 md:w-full md:h-full md:inset-0 md:object-cover md:object-right"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.82) 75%, rgba(0,0,0,0.97) 100%)' }}></div>
          {/* Mobile content — vertically centered inside the shorter image area */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-3 md:hidden">
            <h1 className="whitespace-nowrap font-bold leading-tight tracking-tight mb-1 text-[1.15rem]" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Interactive Mind Reading</h1>
            <p className="text-xs font-semibold mb-2" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>for Corporate Events</p>
            <p className="text-[9px] leading-snug text-white/90 max-w-[290px] mx-auto mb-2" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.85)' }}>Whether you&apos;re hosting a conference, awards banquet, client appreciation event, or holiday party, Ryan Daniels creates unforgettable interactive experiences that bring an entire room together through laughter, participation, and impossible moments</p>
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              <button onClick={() => setVideoOpen(true)} className="text-[9px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full border border-white/85 bg-transparent text-white whitespace-nowrap" data-testid="corp-watch-experience-btn-mobile">Watch the Experience</button>
              <button onClick={() => setQuoteOpen(true)} className="text-[9px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full bg-magenta text-white whitespace-nowrap" data-testid="corp-get-quote-btn-mobile">Request Availability</button>
            </div>
          </div>
        </div>
        {/* Desktop content */}
        <div className="hidden md:block relative z-10 max-w-5xl mx-auto text-center px-6">
          <h1 className="whitespace-nowrap font-bold leading-tight tracking-tight mb-2 text-[clamp(1.5rem,5.5vw,4.25rem)]" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Interactive Mind Reading</h1>
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>for Corporate Events</p>
          <p className="hero-subheadline mx-auto px-2" style={{ fontSize: 'clamp(0.95rem, 1.4vw, 1.25rem)' }}>Whether you&apos;re hosting a conference, awards banquet, client appreciation event, or holiday party, Ryan Daniels creates unforgettable interactive experiences that bring an entire room together through laughter, participation, and impossible moments</p>
          <div className="mt-8 md:mt-12 flex items-center justify-center gap-3 md:gap-4 flex-wrap">
            <button onClick={() => setVideoOpen(true)} className="cta-button-outline large text-sm md:text-base" data-testid="corp-watch-experience-btn">
              Watch the Experience
            </button>
            <button onClick={() => setQuoteOpen(true)} className="cta-button large text-sm md:text-base" data-testid="corp-get-quote-btn">Request Availability</button>
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-16 px-6 relative z-10 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Trusted By</h2>
            <div className="section-divider"></div>
          </div>
          <div className="logo-scroll-container">
            <div className="logo-scroll">
              {[...Array(2)].flatMap((_, dup) => [
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/9eac6892-84ea-4861-9538-4b88e9b7eb14/IMG_1068.jpg', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/4466ad3a-6f58-49dd-9917-1aee6231cb9e/IMG_1066.png', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/5c77a29c-924e-47e8-93d7-80cdba71bb80/IMG_1063.png', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/73809047-f1f5-4aec-b6d4-f29045a3bc16/IMG_1065.png', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/c4c02014-5957-4314-97b7-6b5a57cc61d7/IMG_1064.jpg', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/f17b089a-a2b5-4898-9b97-9f86f3759162/IMG_1067.jpg', alt: 'Client Logo' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/0dc06ff2-f8be-497d-91ac-ebcee7239241/dc-magic-festival-2024-png.jpg', alt: 'DC Magic Festival 2024' },
                { src: 'https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/768acfd1-944d-4ff6-b8bd-23472bc632e4/Unknown.png', alt: 'Client Logo' },
                { src: 'https://customer-assets.emergentagent.com/job_eb0f6d77-0887-4a47-858f-d49ce1e2b7c3/artifacts/gtejwrkd_images.jpeg', alt: 'Black & White Comedy Productions' },
              ].map((logo, i) => (
                <div key={`${dup}-${i}`} className="logo-item">
                  <img src={logo.src} alt={logo.alt} className="w-full h-20 object-contain" />
                </div>
              )))}
            </div>
          </div>
        </div>
      </section>

      {/* Guests become part of it */}
      <section className="py-20 md:py-24 px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Your guests won&apos;t just watch the show—they become part of it.
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
            Throughout the evening, audience members make impossible predictions, experience mind reading firsthand, and become the stars of unforgettable moments that leave the entire room laughing, participating, and wondering how any of it was possible.
          </p>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-20 md:py-24 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Perfect For</h2>
            <div className="section-divider"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {perfectFor.map((item, i) => (
              <div key={i} className="bg-neutral-mid/40 border border-white/10 rounded-2xl px-6 py-6 text-center hover:border-magenta/50 transition-colors">
                <p className="text-base md:text-lg font-medium text-white">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section className="py-20 md:py-24 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Why It Works For Corporate Events</h2>
            <div className="section-divider"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyItWorks.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-neutral-mid/40 border border-white/10 rounded-2xl p-8 text-center hover:border-magenta/50 transition-colors">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-magenta/15 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-magenta" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full-width photo above testimonials */}
      <div className="relative z-10 w-full overflow-hidden">
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/corporate_stage_2.webp`}
          alt="Ryan Daniels performing at a corporate event"
          className="block w-full h-auto"
        />
      </div>

      {/* Testimonials */}
      <section className="py-20 md:py-24 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">What People Say</h2>
            <div className="section-divider"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="text-center px-2">
                <div className="flex justify-center gap-1 mb-3">
                  {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 text-magenta" fill="currentColor" />)}
                </div>
                <p className="text-sm md:text-base mb-3 italic leading-relaxed text-gray-200">&ldquo;{t.text}&rdquo;</p>
                <p className="font-semibold text-magenta text-sm">{t.author}</p>
                <p className="text-xs text-gray-400">{t.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">FAQ</h2>
            <div className="section-divider"></div>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-neutral-mid/40 border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left hover:bg-white/[0.02] transition-colors"
                  data-testid={`corp-faq-${i}`}
                >
                  <span className="text-base md:text-lg font-medium text-white pr-4">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-magenta shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 md:px-6 md:pb-6 text-gray-300 text-sm md:text-base leading-relaxed border-t border-white/5 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Resources Teaser */}
      <section className="py-12 md:py-14 px-6 relative z-10" data-testid="corp-booking-resources">
        <div className="max-w-2xl mx-auto text-center border-t border-b border-white/10 py-10">
          <p className="text-xs md:text-sm font-semibold tracking-[0.2em] text-magenta uppercase mb-3">Event Planners &amp; Entertainment Professionals</p>
          <p className="text-sm md:text-base text-gray-300 mb-5">Access performance footage, downloadable materials, photos, and booking information</p>
          <a href="/booking" className="inline-block text-sm md:text-base font-semibold tracking-[0.15em] text-white uppercase border-b border-magenta pb-1 hover:text-magenta transition-colors" data-testid="corp-booking-resources-link">
            View Booking Resources →
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 leading-tight" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Ready to create an unforgettable experience for your guests?
          </h2>
          <button onClick={() => setQuoteOpen(true)} className="cta-button large text-sm md:text-base" data-testid="corp-request-availability-btn">Request Availability</button>
          <p className="mt-6 text-gray-400 text-sm md:text-base">
            or email <a href="mailto:ryan@ryandanielsmentalist.com" className="text-magenta hover:text-magenta/80 font-medium underline underline-offset-4" data-testid="corp-email-link">ryan@ryandanielsmentalist.com</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-6 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="body-small text-gray-500">&copy; 2026 Ryan Daniels. All rights reserved.</p>
        </div>
      </footer>

      {/* Video Modal */}
      {videoOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm px-4" onClick={() => setVideoOpen(false)} data-testid="corp-video-modal">
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close video" data-testid="corp-video-close-btn">
              <X size={28} />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-neutral-900">
              <video src={PROMO_VIDEO_URL} autoPlay controls playsInline className="w-full h-full" data-testid="corp-promo-video">
                <source src={PROMO_VIDEO_URL} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Quote / Contact Modal */}
      {quoteOpen && (
        <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center bg-black/90 backdrop-blur-sm px-4 py-8 overflow-y-auto" onClick={() => setQuoteOpen(false)} data-testid="corp-quote-modal">
          <div className="relative w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQuoteOpen(false)} className="absolute -top-2 -right-2 md:-top-4 md:-right-4 z-10 bg-neutral-900 rounded-full p-2 text-white/70 hover:text-white transition-colors" aria-label="Close" data-testid="corp-quote-close-btn">
              <X size={22} />
            </button>
            <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">
              {submitted ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="corp-quote-success">
                  <CheckCircle size={64} className="text-magenta mb-4" />
                  <p className="text-white text-xl font-semibold mb-2">Thanks — your request was sent!</p>
                  <p className="text-gray-400">Ryan will be in touch shortly.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Request a Quote</h3>
                  <p className="text-gray-400 text-sm mb-6">Tell me about your event — I&apos;ll be in touch within 24 hours.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input name="name" placeholder="Name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-name" />
                      <input name="company" placeholder="Company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-company" />
                      <input name="email" type="email" placeholder="Email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-email" />
                      <input name="phone" type="tel" placeholder="Phone Number" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-phone" />
                    </div>
                    <select name="eventType" required value={formData.eventType} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-event-type">
                      <option value="">Event Type</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <div className="relative">
                      <button type="button" onClick={() => setShowCal(!showCal)} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-left text-sm flex items-center justify-between focus:outline-none focus:border-magenta" data-testid="corp-quote-date-btn">
                        <span className={formData.eventDate ? 'text-white' : 'text-gray-500'}>
                          {formData.eventDate ? format(formData.eventDate, 'PPP') : 'Preferred Event Date'}
                        </span>
                        <CalendarIcon size={18} className="text-magenta" />
                      </button>
                      {showCal && (
                        <div className="absolute z-20 mt-2 bg-neutral-900 border border-white/10 rounded-xl shadow-2xl">
                          <Calendar
                            mode="single"
                            selected={formData.eventDate}
                            onSelect={(d) => { setFormData((p) => ({ ...p, eventDate: d })); setShowCal(false); }}
                          />
                        </div>
                      )}
                    </div>
                    <select name="audienceSize" required value={formData.audienceSize} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-audience">
                      <option value="">Estimated Audience Size</option>
                      {AUDIENCE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input name="eventLocation" placeholder="Event Location" required value={formData.eventLocation} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-location" />
                    <textarea name="message" placeholder="Tell Me More about Your Event" required rows={5} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm resize-none" data-testid="corp-quote-message" />
                    <select name="heardFrom" required value={formData.heardFrom} onChange={handleChange} className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-magenta text-sm" data-testid="corp-quote-heard-from">
                      <option value="">How Did You Hear About Me</option>
                      {HEARD_FROM.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <button type="submit" disabled={submitting} className="w-full py-3 bg-magenta hover:bg-magenta/90 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="corp-quote-submit">
                      {submitting ? 'Sending…' : (<><Send size={18} />Send Request</>)}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Corporate;
